import { useCallback } from "react";
import { filesApi } from "../api";
import { getStoredUser, isExpiredTokenResponse } from "../lib/auth";

interface UseFolderContentOptions {
  onSessionExpired?: () => void;
}

export const useFolderContent = ({
  onSessionExpired,
}: UseFolderContentOptions = {}) => {
  const getEmail = useCallback(() => getStoredUser().email || null, []);

  const guardExpiredToken = useCallback(
    <T>(data: T) => {
      if (isExpiredTokenResponse(data)) {
        onSessionExpired?.();
      }

      return data;
    },
    [onSessionExpired],
  );

  const getFolderInfo = useCallback(
    async (path: string) =>
      guardExpiredToken(await filesApi.getFolderByPath(path, getEmail())),
    [getEmail, guardExpiredToken],
  );

  const getInitialFiles = useCallback(
    async (path: string, size: string) =>
      guardExpiredToken(
        await filesApi.getFolderContent({
          email: getEmail(),
          path,
          size,
          page: 0,
        }),
      ),
    [getEmail, guardExpiredToken],
  );

  const getFiles = useCallback(
    async (path: string, size: string, page: number) =>
      guardExpiredToken(
        await filesApi.getFolderContent({
          email: getEmail(),
          path,
          size,
          page,
        }),
      ),
    [getEmail, guardExpiredToken],
  );

  const getTotalFiles = useCallback(
    async (path: string) =>
      guardExpiredToken(await filesApi.getTotalFolderContent(path, getEmail())),
    [getEmail, guardExpiredToken],
  );

  const getFolders = useCallback(
    async (path: string, size: string) =>
      guardExpiredToken(await filesApi.getFolders({ path, size })),
    [guardExpiredToken],
  );

  const getPathNames = useCallback(
    async (path: string) =>
      path === "/"
        ? []
        : guardExpiredToken(await filesApi.getFolderNames(path)),
    [guardExpiredToken],
  );

  const moveToTrash = useCallback(
    async (id: number) =>
      guardExpiredToken(await filesApi.moveToTrash(id, getEmail())),
    [getEmail, guardExpiredToken],
  );

  return {
    getFolderInfo,
    getInitialFiles,
    getFiles,
    getTotalFiles,
    getFolders,
    getPathNames,
    moveToTrash,
  };
};
